/**
 * SearchForm Component
 *
 * Simple search form for manually searching SAP contacts
 * Single input searches across: phone, name, email, account name, contact ID
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SearchForm.module.css';

export interface SearchParams {
  query: string;
}

interface SearchFormProps {
  onSearch: (params: SearchParams) => void;
  isLoading?: boolean;
}

export function SearchForm({ onSearch, isLoading = false }: SearchFormProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const canSearch = query.trim() && !isLoading;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (canSearch) {
        onSearch({ query: query.trim() });
      }
    },
    [query, canSearch, onSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canSearch) {
        e.preventDefault();
        onSearch({ query: query.trim() });
      }
    },
    [query, canSearch, onSearch]
  );

  return (
    <div className={styles.searchForm}>
      <div className={styles.searchHeader}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
        </svg>
        <span>{t('call:sap.searchSapContacts')}</span>
      </div>
      <form onSubmit={handleSubmit} className={styles.searchInputRow}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('call:sap.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button type="submit" className={styles.searchButton} disabled={!canSearch}>
          {isLoading ? (
            <div className={styles.miniSpinner} />
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}

export default SearchForm;
